import React, { useState, useContext, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Alert,
  AlertTitle,
  LinearProgress,
  Card,
  CardContent,
  useTheme,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Chip,
  alpha,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GetAppIcon from '@mui/icons-material/GetApp';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { FaTruck, FaGlobeEurope, FaBoxOpen } from 'react-icons/fa';
import { useProcess } from '../context/ProcessContext';
import { NotificationContext } from '../context/NotificationContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { API_ENDPOINTS } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS } from '../constants/colors';

const ACCENT = TOOL_COLORS.EXCEL.primary;

const EUFreight = () => {
  const theme = useTheme();
  const { addProcess, updateProcess } = useProcess();
  const { addStructuredNotification, showNotification } = useContext(NotificationContext);
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const [uploadedFile, setUploadedFile] = useState(null);
  const [carrier, setCarrier] = useState('Both');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [processId, setProcessId] = useState(null);
  const [status, setStatus] = useState(null);

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  const validateFile = (file) => {
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    return ['.xlsx', '.xls', '.csv'].includes(ext);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!validateFile(file)) {
      setError('Please upload a valid Excel (.xlsx, .xls) or CSV file');
      return;
    }
    setUploadedFile(file);
    setError('');
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!validateFile(file)) {
      setError('Please upload a valid Excel (.xlsx, .xls) or CSV file');
      return;
    }
    setUploadedFile(file);
    setError('');
  }, []);

  const downloadResult = useCallback(async (id) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(
        `${API_ENDPOINTS.EU_FREIGHT_DOWNLOAD(id)}?t=${Date.now()}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      let filename = 'EU_Freight.xlsx';
      if (disposition) {
        const m = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (m) filename = m[1].replace(/['"]/g, '');
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        if (document.body.contains(a)) document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  }, []);

  // Declared before the effect that uses it — a useEffect referencing a
  // still-uninitialised useCallback hits the temporal dead zone and the whole
  // component fails to mount silently.
  const fetchStatus = useCallback(async () => {
    const currentProcessId = processIdRef.current;
    if (!currentProcessId) return;

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(API_ENDPOINTS.EU_FREIGHT_STATUS(currentProcessId), {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!data.success) return;

      const s = data.status;
      setStatus(s);

      updateActivityProcess(currentProcessId, {
        progressPercentage: s.progressPercentage || 0,
        totalItems: s.totalItems || 0,
        processedItems: s.processedItems || 0,
      });
      if (s.logs?.length) setActivityLogs(currentProcessId, s.logs);
      updateProcess(currentProcessId, {
        progress: s.progressPercentage || 0,
        status: s.status,
      });

      if (s.status === 'completed' || s.status === 'error') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        clearIntervalRef(currentProcessId);
        setLoading(false);
        completeActivityProcess(currentProcessId, s.status === 'completed');

        if (s.status === 'completed' && !downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          if (s.resultFileUrl) {
            const link = document.createElement('a');
            link.href = s.resultFileUrl;
            link.download = s.resultFileName || 'EU_Freight.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.open(s.resultFileUrl, '_blank');
          } else {
            downloadResult(currentProcessId);
          }

          const money = (v) =>
            Number(v || 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          const totals = [];
          if (s.upsPriced > 0) totals.push(`UPS EUR ${money(s.upsTotal)}`);
          if (s.fedexPriced > 0) totals.push(`FedEx EUR ${money(s.fedexTotal)}`);
          setResult(
            `Priced ${s.matchedItems} item${s.matchedItems === 1 ? '' : 's'}` +
            (totals.length ? ` — estimated total ${totals.join(' · ')}.` : '.')
          );
        } else if (s.status === 'error') {
          setError('Freight calculation failed. Check the logs below for details.');
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  }, [updateActivityProcess, setActivityLogs, updateProcess, clearIntervalRef,
      completeActivityProcess, downloadResult]);

  useEffect(() => {
    if (processId && loading) {
      intervalRef.current = setInterval(() => fetchStatus(), 1000);
      setIntervalRef(processId, intervalRef.current);
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
    return undefined;
  }, [loading, processId, fetchStatus, setIntervalRef]);

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const handleCalculate = async () => {
    if (!uploadedFile) {
      setError('Please upload a file containing ItemIDs');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    setStatus(null);
    downloadTriggeredRef.current = false;

    const formData = new FormData();
    formData.append('inputFile', uploadedFile);
    formData.append('carrier', carrier);

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(API_ENDPOINTS.EU_FREIGHT_CALCULATE, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to start freight calculation');

      setProcessId(data.processId);
      processIdRef.current = data.processId;
      addActivityProcess(data.processId, 'EU Freight', 'EU Freight');
      addProcess({
        id: data.processId,
        name: 'EU Freight',
        progress: 0,
        status: 'processing',
      });
    } catch (err) {
      setError(err.message);
      setLoading(false);
      showNotification(`Error: ${err.message}`, 'error');
    }
  };

  // Platform convention: template files are stamped in Eastern time, matching
  // what the backend writes, so a user's downloads sort together sensibly.
  const easternStamp = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      month: 'short', day: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    }).formatToParts(new Date()).reduce((a, p) => ({ ...a, [p.type]: p.value }), {});
    return `${parts.month} ${parts.day} ${parts.year}, ${parts.hour}-${parts.minute}-${parts.second} ${parts.dayPeriod}`;
  };

  const downloadTemplate = () => {
    const blob = new Blob(['ItemID,Packaging Type\nABT141A-5,Plastic\nABT141D-8,Box\nADR101A-3,\n'], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `EU Freight Template ${easternStamp()}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    showNotification('Template downloaded', 'success');
  };

  const resetForm = () => {
    setUploadedFile(null);
    setCarrier('Both');
    setError('');
    setResult(null);
    setProcessId(null);
    processIdRef.current = null;
    downloadTriggeredRef.current = false;
    setStatus(null);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const carrierOptions = ['Both', 'UPS only', 'FedEx only'];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="EU Freight" category="excel" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 1,
          }}
        >
          EU FREIGHT
        </Typography>
        <Typography
          variant="body2"
          sx={{ textAlign: 'center', color: theme.palette.text.secondary, mb: 4 }}
        >
          UPS and FedEx estimates into Europe — upload ItemIDs, dimensions come from the EU item file
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Freight Calculation
              </Typography>

              <Card sx={{ mb: 3, bgcolor: alpha(ACCENT, 0.04) }}>
                <CardContent sx={{ pb: 2 }}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel
                      component="legend"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 1.5,
                        '&.Mui-focused': { color: theme.palette.text.primary },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaTruck />
                        <span>Carrier</span>
                      </Box>
                    </FormLabel>
                    <RadioGroup row value={carrier} onChange={(e) => setCarrier(e.target.value)}>
                      {carrierOptions.map((label) => (
                        <FormControlLabel
                          key={label}
                          value={label.split(' ')[0]}
                          control={
                            <Radio size="small" sx={{ '&.Mui-checked': { color: ACCENT } }} />
                          }
                          label={<Typography variant="body2">{label}</Typography>}
                          sx={{ mr: 3 }}
                        />
                      ))}
                    </RadioGroup>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 0.5, color: theme.palette.text.secondary }}
                    >
                      With <strong>Both</strong>, every item is priced twice and the result names
                      the cheaper carrier and what it saves.
                    </Typography>
                  </FormControl>
                </CardContent>
              </Card>

              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('eu-freight-file-input').click()}
                sx={{
                  border: `2px dashed ${
                    isDragOver ? ACCENT : uploadedFile ? '#4CAF50' : theme.palette.divider
                  }`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: isDragOver
                    ? alpha(ACCENT, 0.08)
                    : uploadedFile
                      ? theme.palette.mode === 'dark'
                        ? 'rgba(76, 175, 80, 0.15)'
                        : 'rgba(76, 175, 80, 0.06)'
                      : alpha(theme.palette.action.hover, 0.02),
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': { borderColor: ACCENT, bgcolor: alpha(ACCENT, 0.04) },
                }}
              >
                <input
                  id="eu-freight-file-input"
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                />
                {uploadedFile ? (
                  <Box>
                    <CheckCircleIcon sx={{ fontSize: 40, color: '#4CAF50', mb: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {uploadedFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      {(uploadedFile.size / 1024).toFixed(1)} KB
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedFile(null);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon
                      sx={{ fontSize: 40, color: theme.palette.text.secondary, mb: 1 }}
                    />
                    <Typography variant="body1">
                      Drop your file here, or click to browse
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Excel or CSV with an <strong>ItemID</strong> column and an optional <strong>Packaging Type</strong> (Plastic / Box)
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<CalculateIcon />}
                  onClick={handleCalculate}
                  disabled={loading || !uploadedFile}
                  sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: TOOL_COLORS.EXCEL.hover } }}
                >
                  {loading ? 'Calculating…' : 'Calculate Freight'}
                </Button>
                <Button variant="outlined" startIcon={<GetAppIcon />} onClick={downloadTemplate}>
                  Download Template
                </Button>
                <Button variant="text" startIcon={<RestartAltIcon />} onClick={resetForm}>
                  Reset
                </Button>
              </Box>

              {loading && status && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">
                      {status.processedItems} of {status.totalItems} items
                    </Typography>
                    <Typography variant="body2">
                      {Math.round(status.progressPercentage || 0)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant={status.totalItems ? 'determinate' : 'indeterminate'}
                    value={status.progressPercentage || 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}

              {/* No log panel here — the run's logs go to the global activity
                  window via setActivityLogs, and duplicating them inside the
                  interface just competes with it. */}

              {error && (
                <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {result && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  <AlertTitle>Done</AlertTitle>
                  {result}
                  {status && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`${status.matchedItems} priced`} color="success" />
                      {status.unmatchedItems > 0 && (
                        <Chip
                          size="small"
                          label={`${status.unmatchedItems} without dimensions`}
                          color="warning"
                        />
                      )}
                      {status.overLimit > 0 && (
                        <Chip
                          size="small"
                          label={`${status.overLimit} outside carrier limits`}
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  )}
                </Alert>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Alert severity="info" icon={<InfoIcon fontSize="small" />}>
                <Typography variant="caption" component="div">
                  Rates are the <strong>weighted average</strong> across the European destination
                  zones, so this is an estimate — a real invoice varies with the country.
                </Typography>
              </Alert>

              <Alert severity="info" sx={{ mt: 1.5 }}>
                <Typography variant="caption" component="div">
                  Address correction and remote-area fees are <strong>expected-occurrence</strong>
                  {' '}figures — 1% of the fee, not the fee itself — which is why a single item&rsquo;s
                  total carries fractions of a cent. That is how both source workbooks price them.
                </Typography>
              </Alert>

              <Alert severity="warning" sx={{ mt: 1.5 }}>
                <Typography variant="caption" component="div">
                  Items past a carrier&rsquo;s limits get no rate. UPS charges a EUR 545 penalty and
                  FedEx EUR 250; those rows are left blank with the reason spelled out, rather than
                  priced as though they would ship.
                </Typography>
              </Alert>

              <Alert severity="warning" sx={{ mt: 1.5 }}>
                <Typography variant="caption" component="div">
                  One parcel per item. If an item ships as several cartons, list each child ItemID
                  on its own row.
                </Typography>
              </Alert>

              <Divider sx={{ my: 2.5 }} />

              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <InfoIcon fontSize="small" /> How it works
              </Typography>
              <List dense disablePadding>
                {[
                  'Upload a file with an ItemID column. An optional Packaging Type column (Plastic or Box) overrides the packaging per item; leave it blank to use the EU item file.',
                  'Weight and package dimensions come from the EU item file on S3, already in kg and cm.',
                  'Without a Packaging Type, Boxed Packaging decides: YES ships as a box, anything else — blanks included — as plastic.',
                  'Billed weight is the greater of actual and dimensional weight, with a floor for oversized parcels.',
                  'Only the largest oversize surcharge applies, never several at once.',
                  'Output lists the dimensions, the billed weight and every charge as its own column.',
                ].map((text, i) => (
                  <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                      {i === 2 ? (
                        <FaBoxOpen size={12} color={ACCENT} />
                      ) : (
                        <FaGlobeEurope size={12} color={ACCENT} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ variant: 'body2' }}
                      primary={text}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default EUFreight;
